import React from 'react';
import { Link } from 'react-router-dom';
import { CpuChipIcon, LightBulbIcon, AcademicCapIcon, ArrowRightIcon, CodeBracketIcon, PresentationChartBarIcon, ChatBubbleOvalLeftEllipsisIcon, UsersIcon, CheckBadgeIcon, WrenchScrewdriverIcon, ScaleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import techImage from '../img/tech1.jpg';

// Consistent PageHeader for detail pages
const PageHeader = ({ title, subtitle, breadcrumbs }) => (
  <div className="bg-figma-dark-bg-secondary py-16 md:py-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      {breadcrumbs && (
        <nav className="text-sm mb-4 animate-fade-in-up">
          {breadcrumbs.map((crumb, index) => (
            <span key={index} className="text-figma-dark-text-secondary/80">
              {index > 0 && <span className="mx-2">/</span>}
              {crumb.to ? (
                <Link to={crumb.to} className="hover:text-figma-dark-primary transition-colors">{crumb.label}</Link>
              ) : (
                <span>{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-figma-dark-text-primary animate-fade-in-up" style={{animationDelay: '0.1s'}}>{title}</h1>
      {subtitle && <p className="mt-4 text-lg text-figma-dark-text-secondary max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.2s'}}>{subtitle}</p>}
    </div>
  </div>
);

// Card for features/capabilities - Figma Dark Style
const FeatureCard = ({ icon: Icon, title, description, animationDelay }) => (
  <div className="bg-figma-dark-bg-primary border border-figma-dark-border rounded-lg p-6 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center text-center hover:animate-subtle-3d-tilt-hover animate-fade-in-up" style={{animationDelay}}>
    <Icon className="h-12 w-12 text-figma-dark-primary mb-5" />
    <h3 className="text-xl font-semibold text-figma-dark-text-primary mb-2">{title}</h3>
    <p className="text-base text-figma-dark-text-secondary leading-relaxed flex-grow">{description}</p>
  </div>
);

// Section component for consistent styling - Figma Dark Style
const ContentSection = ({ title, children, className = '' }) => (
  <section className={`py-12 md:py-16 ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {title && <h2 className="text-2xl md:text-3xl font-semibold text-figma-dark-text-primary text-center mb-10 md:mb-12 animate-fade-in-up">{title}</h2>}
      {children}
    </div>
  </section>
);

const AIDevelopmentPage = () => {
  const breadcrumbs = [
    { label: 'Home', to: '/' },
    { label: 'Solutions', to: '/solutions' }, 
    { label: 'AI & Machine Learning', to: '/solutions/ai-development' }
  ];

  const services = [
    {
      title: "Deep Learning Systems",
      description: "Build and deploy sophisticated deep learning models for complex pattern recognition and decision-making tasks.",
      icon: "🧠",
      features: [
        "Neural Networks",
        "Model Training",
        "Transfer Learning",
        "Model Optimization"
      ]
    },
    {
      title: "AI-Powered Analytics",
      description: "Transform your data into actionable insights with advanced AI analytics and predictive modeling.",
      icon: "📊",
      features: [
        "Predictive Analytics",
        "Pattern Recognition",
        "Data Visualization",
        "Business Intelligence"
      ]
    },
    {
      title: "Natural Language Processing",
      description: "Develop intelligent systems that understand, interpret, and generate human language.",
      icon: "💬",
      features: [
        "Text Analysis",
        "Language Models",
        "Sentiment Analysis",
        "Chatbot Development"
      ]
    },
    {
      title: "Reinforcement Learning",
      description: "Create adaptive systems that learn and improve through interaction with their environment.",
      icon: "🎯",
      features: [
        "Decision Making",
        "Policy Optimization",
        "Multi-agent Systems",
        "Autonomous Learning"
      ]
    }
  ];

  const capabilities = [
    {
      category: "Machine Learning",
      items: ["Supervised Learning", "Unsupervised Learning", "Semi-supervised Learning", "Federated Learning"]
    },
    {
      category: "Computer Vision",
      items: ["Object Detection", "Image Classification", "Video Analysis", "Facial Recognition"]
    },
    {
      category: "AI Infrastructure",
      items: ["GPU Acceleration", "Distributed Training", "Model Serving", "Edge AI"]
    },
    {
      category: "AI Ethics",
      items: ["Bias Detection", "Explainable AI", "Privacy Protection", "Fairness Metrics"]
    }
  ];

  return (
    <div className="min-h-screen bg-figma-dark-bg-primary text-figma-dark-text-primary">
      {/* Hero Section */}
      <section 
        className="relative min-h-[60vh] bg-cover bg-center flex items-center justify-center"
        style={{ backgroundImage: `url(${techImage})` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 text-center px-4 py-16">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-figma-dark-primary to-figma-dark-accent bg-clip-text text-transparent">
              AI Development
            </span>
          </h1>
          <p className="text-xl text-figma-dark-text-secondary max-w-3xl mx-auto">
            Advanced AI solutions for intelligent business transformation
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              className="bg-figma-dark-bg-secondary/30 backdrop-blur-sm border border-figma-dark-border rounded-xl p-8 hover:shadow-figma-dark-glow transition-all duration-300"
            >
              <div className="text-5xl mb-6">{service.icon}</div>
              <h3 className="text-2xl font-semibold mb-4 text-white">{service.title}</h3>
              <p className="text-figma-dark-text-secondary mb-6">
                {service.description}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {service.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-sm text-figma-dark-text-secondary">
                    <span className="text-figma-dark-primary">•</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-20 px-4 bg-figma-dark-bg-secondary/20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-white">AI Capabilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {capabilities.map((capability, index) => (
              <div 
                key={index}
                className="bg-figma-dark-bg-secondary/30 backdrop-blur-sm border border-figma-dark-border rounded-xl p-6"
              >
                <h3 className="text-xl font-semibold mb-4 text-white">{capability.category}</h3>
                <div className="flex flex-wrap gap-2">
                  {capability.items.map((item, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-figma-dark-primary/10 text-figma-dark-primary rounded-full text-sm"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6 text-white">Ready to Harness the Power of AI?</h2>
          <p className="text-figma-dark-text-secondary mb-8">
            Let's discuss how our AI solutions can transform your business operations
          </p>
          <Link
            to="/contact"
            className="px-8 py-4 rounded-full bg-gradient-to-r from-figma-dark-primary to-figma-dark-accent text-white font-medium hover:shadow-figma-dark-glow transition-all duration-300 hover:scale-105"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AIDevelopmentPage;